/**
 * Tajweed Rules Drawer Component
 * แสดงกฎและสีจริงจาก QCF v4 — กดขยาย accordion เพื่อดูตัวอย่างอายะฮฺ
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { BookOpen, Play, Pause, Loader2 } from 'lucide-react';
import { useTheme } from '@/components/ui/theme-provider';
import { getAyahWithWords, getVerseAudio } from '@/services/quran.service';
import { useQcfFont } from '@/hooks/useQcfFont';
import QuranWord from './QuranWord';
import type { Ayah } from '@/types/quran';

interface TajweedRulesDrawerProps {
  open: boolean;
  onClose: () => void;
  onToggle?: () => void;
  /** If false, the floating button will be hidden */
  isEnabled?: boolean;
}

interface FloatingButtonProps {
  onClick: () => void;
}

interface TajweedRule {
  name: string;
  nameArabic: string;
  description: string;
  color: string;
  darkColor: string;
  exampleSurah: number;
  exampleAyah: number;
}

/**
 * Real tajweed colors verified from quran.com legend (QCF v4 COLRv1 font)
 */
const tajweedRules: TajweedRule[] = [
  {
    name: 'ไม่ออกเสียง (Silent)',
    nameArabic: 'الحرف الصامت',
    description: 'ตัวอักษรที่เขียนแต่ไม่ออกเสียง',
    color: '#a5a5a5',
    darkColor: '#b5b5b5',
    exampleSurah: 1,
    exampleAyah: 5,
  },
  {
    name: 'มัดปกติ 2 จังหวะ',
    nameArabic: 'المد الطبيعي',
    description: 'ลากเสียงยาว 2 จังหวะ (Normal madd)',
    color: '#ce9e00',
    darkColor: '#e0b420',
    exampleSurah: 2,
    exampleAyah: 2,
  },
  {
    name: 'มัดมุนฟะศิล 2/4/6',
    nameArabic: 'المد المنفصل',
    description: 'ลากยาว 2, 4, หรือ 6 จังหวะ (Separated madd)',
    color: '#ff7b00',
    darkColor: '#ff9933',
    exampleSurah: 2,
    exampleAyah: 164,
  },
  {
    name: 'มัดมุตตะศิล 4/5',
    nameArabic: 'المد المتصل',
    description: 'ลากยาว 4-5 จังหวะ (Connected madd)',
    color: '#f40000',
    darkColor: '#ff3333',
    exampleSurah: 2,
    exampleAyah: 6,
  },
  {
    name: 'มัดลาซิม 6 จังหวะ',
    nameArabic: 'المد اللازم',
    description: 'จำเป็นต้องลากยาว 6 จังหวะ (Necessary madd)',
    color: '#b50000',
    darkColor: '#d42020',
    exampleSurah: 2,
    exampleAyah: 1,
  },
  {
    name: 'กุนนะฮฺ / อิคฟาอฺ',
    nameArabic: 'الغنة / الإخفاء',
    description: 'เสียงขึ้นจมูกและซ่อนเสียง (Ghunna / Ikhfa\')',
    color: '#09b000',
    darkColor: '#2ecc30',
    exampleSurah: 99,
    exampleAyah: 7,
  },
  {
    name: 'กลกละฮฺ (Qalqala)',
    nameArabic: 'القلقلة',
    description: 'เสียงสะท้อนเมื่อมีซุกูน (ق ط ب ج د)',
    color: '#2fadff',
    darkColor: '#5fc3ff',
    exampleSurah: 112,
    exampleAyah: 1,
  },
  {
    name: 'ตัฟคีม (Tafkhim)',
    nameArabic: 'التفخيم',
    description: 'ตัวอักษรหนัก เช่น ص ض ط ظ ق ร',
    color: '#3f48e6',
    darkColor: '#6670f0',
    exampleSurah: 1,
    exampleAyah: 1,
  },
];

// Floating Button
function TajweedFloatingButton({ onClick }: FloatingButtonProps) {
  return (
    <button
      onClick={onClick}
      className="fixed right-0 top-1/2 -translate-y-1/2 z-40 
                 bg-gradient-to-l from-primary to-primary/90 
                 text-primary-foreground
                 px-4 py-2 rounded-l-xl shadow-lg
                 hover:px-4 transition-all duration-300
                 flex items-center gap-2"
      style={{ writingMode: 'vertical-rl' }}
    >
      <span className="text-sm font-semibold">กฎตัจวีด</span>
    </button>
  );
}

export function TajweedRulesDrawer({ open, onClose, onToggle, isEnabled = false }: TajweedRulesDrawerProps) {
  const { effectiveTheme } = useTheme();
  const [ayahs, setAyahs] = useState<Map<string, Ayah>>(new Map());
  const [playingAyah, setPlayingAyah] = useState<string | null>(null);
  const [loadingAudio, setLoadingAudio] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Collect words for font loading — only from expanded ayahs
  const allWords = useMemo(() => {
    const words: Ayah['words'] = [];
    for (const key of expandedItems) {
      const rule = tajweedRules[parseInt(key)];
      if (!rule) continue;
      const ayah = ayahs.get(`${rule.exampleSurah}:${rule.exampleAyah}`);
      if (ayah?.words) words.push(...ayah.words);
    }
    return words;
  }, [ayahs, expandedItems]);

  const loadedPages = useQcfFont(allWords, { version: 'v4', theme: effectiveTheme });

  // Fetch ayah data when an accordion item is expanded
  useEffect(() => {
    if (!open) return;
    expandedItems.forEach((idx) => {
      const rule = tajweedRules[parseInt(idx)];
      if (!rule) return;
      const key = `${rule.exampleSurah}:${rule.exampleAyah}`;
      if (ayahs.has(key)) return; // already loaded
      getAyahWithWords(rule.exampleSurah, rule.exampleAyah)
        .then((ayah) => {
          setAyahs((prev) => {
            const next = new Map(prev);
            next.set(key, ayah);
            return next;
          });
        })
        .catch((err) => console.error('Failed to fetch ayah:', err));
    });
  }, [open, expandedItems, ayahs]);

  // Cleanup audio on close
  useEffect(() => {
    if (!open) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingAyah(null);
      setLoadingAudio(null);
    }
  }, [open]);

  const handlePlayAudio = async (surah: number, ayah: number) => {
    const key = `${surah}:${ayah}`;
    if (playingAyah === key && audioRef.current) {
      audioRef.current.pause();
      setPlayingAyah(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();

    try {
      setLoadingAudio(key);
      const url = await getVerseAudio(surah, ayah, '7');
      if (!url) { setLoadingAudio(null); return; }

      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setPlayingAyah(null); audioRef.current = null; };
      audio.onerror = () => { setPlayingAyah(null); setLoadingAudio(null); audioRef.current = null; };
      await audio.play();
      setPlayingAyah(key);
    } catch (e) {
      console.error('Failed to play audio:', e);
    } finally {
      setLoadingAudio(null);
    }
  };

  return (
    <>
      {!open && onToggle && isEnabled && <TajweedFloatingButton onClick={onToggle} />}

      {/* Overlay */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm"
          onClick={onClose}
        />
      )}

      {/* Bottom-sheet drawer */}
      <div
        className={`fixed left-0 right-0 bottom-0 z-50 bg-background border-t rounded-t-2xl shadow-xl overflow-hidden transition-transform duration-300 ease-in-out ${
          open ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{ maxHeight: '70vh' }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b z-10 px-4 py-3">
          <div className="w-8 h-1 rounded-full bg-muted-foreground/30 mx-auto mb-2" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-semibold">สีตัจวีด (Tajweed Colors)</h2>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
              <span className="sr-only">ปิด</span>
              ✕
            </Button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto px-4 py-1" style={{ maxHeight: 'calc(70vh - 60px)' }}>
          <Accordion
            type="multiple"
            value={expandedItems}
            onValueChange={setExpandedItems}
            className="w-full"
          >
            {tajweedRules.map((rule, index) => {
              const ayahKey = `${rule.exampleSurah}:${rule.exampleAyah}`;
              const ayah = ayahs.get(ayahKey);
              const isPlaying = playingAyah === ayahKey;
              const isLoadingAudio = loadingAudio === ayahKey;

              return (
                <AccordionItem key={index} value={String(index)} className="border-b last:border-0">
                  {/* Trigger row: color dot + names */}
                  <AccordionTrigger className="py-2.5 hover:no-underline gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0 text-left">
                      <div
                        className="w-4 h-4 rounded-full shrink-0 ring-1 ring-black/10 dark:ring-white/15"
                        style={{ backgroundColor: rule.color }}
                      />
                      <span className="text-sm font-medium truncate">{rule.name}</span>
                      <span className="text-[11px] text-muted-foreground shrink-0 ml-auto mr-1" dir="rtl">
                        {rule.nameArabic}
                      </span>
                    </div>
                  </AccordionTrigger>

                  {/* Expandable content: description + ayah example */}
                  <AccordionContent className="pb-3 pt-0">
                    <p className="text-xs text-muted-foreground mb-2 pl-7">
                      {rule.description}
                    </p>

                    {/* Ayah example */}
                    <div className="ml-7 rounded-lg bg-muted/30 p-2.5">
                      {ayah && ayah.words?.length ? (
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] text-muted-foreground">
                              ตัวอย่าง: {rule.exampleSurah}:{rule.exampleAyah}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handlePlayAudio(rule.exampleSurah, rule.exampleAyah)}
                              disabled={isLoadingAudio}
                              className="h-6 w-6 p-0"
                            >
                              {isLoadingAudio ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : isPlaying ? (
                                <Pause className="w-3.5 h-3.5" />
                              ) : (
                                <Play className="w-3.5 h-3.5" />
                              )}
                            </Button>
                          </div>
                          <div
                            className="leading-loose flex flex-wrap justify-end gap-0.5"
                            dir="rtl"
                            style={{ fontSize: '1.3rem', lineHeight: '2.2' }}
                          >
                            {ayah.words.map((word, wIdx) => (
                              <QuranWord
                                key={`${rule.exampleSurah}-${rule.exampleAyah}-w-${wIdx}`}
                                word={word}
                                isFontLoaded={loadedPages.has(word.pageNumber ?? 0)}
                                fontVersion="v4"
                                theme={effectiveTheme}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (
                        <Skeleton className="h-10 w-full" />
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          <p className="text-[11px] text-center text-muted-foreground py-3">
            เปิดโหมดตัจวีดในการตั้งค่าเพื่อแสดงสีบนข้อความอัลกุรอาน
          </p>
        </div>
      </div>
    </>
  );
}

export default TajweedRulesDrawer;
